import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Link,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FilterChips from '../components/domain/FilterChips';
import EmptyState from '../components/domain/EmptyState';
import api from '../api/client';

const FILTERS = [
  { value: 'all', label: 'Wszyscy' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'it', label: 'IT' },
  { value: 'hr', label: 'HR' },
  { value: 'active', label: 'Aktywni' },
  { value: 'invited', label: 'Zaproszeni' },
];

const STATUS_MAP = {
  active: { label: 'Aktywny', color: 'success' },
  inactive: { label: 'Nieaktywny', color: 'default' },
  invited: { label: 'Zaproszony', color: 'info' },
  blocked: { label: 'Zablokowany', color: 'error' },
};

function Employees() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canInvite = ['admin', 'hr', 'platformadmin'].includes(user?.role);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeFilters, setActiveFilters] = useState(['all']);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDisplayName, setInviteDisplayName] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/api/tenants/me/users')
      .then((data) => {
        const users = data.users || data;
        if (Array.isArray(users)) {
          setEmployees(users.map((u) => ({
            id: u.id,
            name: u.name || u.email,
            email: u.email,
            department: u.department || u.role || '—',
            status: u.status || 'active',
            campaigns: u.campaigns_count || 0,
          })));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleToggleFilter = (value) => {
    if (value === 'all') {
      setActiveFilters(['all']);
      return;
    }
    const next = activeFilters.filter((f) => f !== 'all');
    if (next.includes(value)) {
      const updated = next.filter((f) => f !== value);
      setActiveFilters(updated.length ? updated : ['all']);
    } else {
      setActiveFilters([...next, value]);
    }
  };

  const filteredEmployees = activeFilters.includes('all')
    ? employees
    : employees.filter((e) => {
        return activeFilters.some((f) =>
          f === (e.department || '').toLowerCase() || f === e.status
        );
      });

  const handleOpenDialog = (tab = 0) => {
    setDialogTab(tab);
    setInviteEmail('');
    setInviteDisplayName('');
    setCsvFile(null);
    setCsvText('');
    setInviteResults([]);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const parseCsvRows = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const rows = [];
    let startIndex = 0;
    const first = lines[0].toLowerCase();
    if (first.includes('email')) {
      startIndex = 1;
    }
    for (let i = startIndex; i < lines.length; i++) {
      const parts = lines[i].split(',').map((s) => s.trim());
      const email = parts[0];
      const displayName = parts[1] || '';
      if (email && email.includes('@')) {
        rows.push({ email, displayName });
      }
    }
    return rows;
  };

  const inviteSingle = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteResults([]);
    try {
      const result = await api.post('/api/auth/invite', {
        email: inviteEmail.trim(),
        role: 'employee',
      });
      setInviteResults([{ email: inviteEmail.trim(), ...result }]);
      setSnackbar({ open: true, message: 'Zaproszenie utworzone.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.body?.error || err.message || 'Błąd zaproszenia', severity: 'error' });
    } finally {
      setInviteLoading(false);
    }
  };

  const inviteCsv = async () => {
    const rows = parseCsvRows(csvText);
    if (rows.length === 0) {
      setSnackbar({ open: true, message: 'Nie znaleziono poprawnych adresów e-mail w pliku.', severity: 'error' });
      return;
    }
    setInviteLoading(true);
    setInviteResults([]);
    const results = [];
    for (const row of rows) {
      try {
        const result = await api.post('/api/auth/invite', {
          email: row.email,
          role: 'employee',
        });
        results.push({ email: row.email, ...result });
      } catch (err) {
        results.push({ email: row.email, error: err.body?.error || err.message || 'Błąd' });
      }
    }
    setInviteResults(results);
    setInviteLoading(false);
    setSnackbar({ open: true, message: `Zaproszono ${results.filter((r) => !r.error).length} z ${rows.length} pracowników.`, severity: 'success' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (event) => setCsvText(event.target?.result || '');
    reader.readAsText(file);
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'Link skopiowany do schowka.', severity: 'success' });
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontFamily: theme.typography.h1.fontFamily, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Pracownicy
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Zarządzaj bazą pracowników organizacji
          </Typography>
        </Box>
        {canInvite && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              size="large"
              onClick={() => handleOpenDialog(1)}
              sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0, borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
            >
              Import CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              size="large"
              onClick={() => handleOpenDialog(0)}
              sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0, borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
            >
              Zaproś pracownika
            </Button>
          </Box>
        )}
      </Box>

      {/* Metric Strip */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {[
          { label: 'Łącznie', value: employees.length, icon: <GroupOutlinedIcon fontSize="small" /> },
          { label: 'Aktywnych w kampaniach', value: employees.filter((e) => e.campaigns > 0).length, icon: <TrendingUpIcon fontSize="small" /> },
          { label: 'Zaproszonych', value: employees.filter((e) => e.status === 'invited').length, icon: <PersonAddIcon fontSize="small" /> },
        ].map((metric) => (
          <Box
            key={metric.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1.5,
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '0 1 auto' },
              borderRadius: `${theme.shape.borderRadius}px`,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
            }}
          >
            <Avatar sx={{ bgcolor: theme.tokens?.semantic?.surface?.brandTint, color: 'primary.main', width: 32, height: 32 }}>
              {metric.icon}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
                {metric.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <FilterChips filters={FILTERS} activeFilters={activeFilters} onToggle={handleToggleFilter} />
      </Box>

      {/* Table / List */}
      {filteredEmployees.length === 0 ? (
        <EmptyState
          icon={<GroupOutlinedIcon sx={{ fontSize: 32 }} />}
          title="Brak pracowników"
          description="Dodaj pracowników importując plik CSV lub za pomocą integracji SCIM."
          actionLabel="Importuj"
          onAction={() => navigate('/import')}
          actionIcon={<PersonAddIcon />}
        />
      ) : isMobile ? (
        // Mobile: list-style cards
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredEmployees.map((emp) => {
            const status = STATUS_MAP[emp.status];
            return (
              <Box
                key={emp.id}
                onClick={() => navigate(`${emp.id}`)}
                sx={{
                  p: 2,
                  borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 40, height: 40, fontWeight: 700, fontSize: '0.875rem' }}>
                  {emp.name.split(' ').map((n) => n[0]).join('')}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {emp.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {emp.department || 'Bez działu'} · {emp.campaigns ?? 0} kampanii
                  </Typography>
                </Box>
                <Chip label={status?.label || emp.status} color={status?.color || 'default'} size="small" />
              </Box>
            );
          })}
        </Box>
      ) : (
        // Desktop: table
        <TableContainer
          component={Paper}
          sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Pracownik</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Dział</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Kampanie</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.map((emp) => {
                const status = STATUS_MAP[emp.status];
                return (
                  <TableRow key={emp.id} hover onClick={() => navigate(`${emp.id}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 32, height: 32, fontWeight: 700, fontSize: '0.75rem' }}>
                          {emp.name.split(' ').map((n) => n[0]).join('')}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{emp.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{emp.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{emp.department || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={status?.label || emp.status} color={status?.color || 'default'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{emp.campaigns ?? 0}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Invite Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Zaproś pracowników</DialogTitle>
        <DialogContent>
          <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ mb: 2 }}>
            <Tab label="Pojedynczo" />
            <Tab label="Import CSV" />
          </Tabs>

          {dialogTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="E-mail"
                type="email"
                fullWidth
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="jan.kowalski@firma.pl"
              />
              <TextField
                label="Imię i nazwisko (opcjonalne)"
                fullWidth
                value={inviteDisplayName}
                onChange={(e) => setInviteDisplayName(e.target.value)}
              />
            </Box>
          )}

          {dialogTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
              >
                Wybierz plik CSV
                <input type="file" accept=".csv,.txt" hidden ref={fileInputRef} onChange={handleFileChange} />
              </Button>
              {csvFile && (
                <Typography variant="body2" color="text.secondary">
                  Wybrano: {csvFile.name}
                </Typography>
              )}
              <TextField
                label="Podgląd / edycja CSV"
                multiline
                rows={6}
                fullWidth
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={`email,displayName\njan.kowalski@firma.pl,Jan Kowalski\nanna.nowak@firma.pl,Anna Nowak`}
              />
            </Box>
          )}

          {inviteResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Wyniki:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {inviteResults.map((r) => (
                  <Box key={r.email} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.email}</Typography>
                    {r.inviteUrl ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Link href={r.inviteUrl} target="_blank" rel="noopener" variant="caption" sx={{ wordBreak: 'break-all' }}>
                          {r.inviteUrl}
                        </Link>
                        <Button size="small" onClick={() => copyLink(r.inviteUrl)}>Kopiuj</Button>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="error">{r.error}</Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Anuluj</Button>
          <Button
            variant="contained"
            disabled={inviteLoading || (dialogTab === 0 ? !inviteEmail.trim() : !csvText.trim())}
            onClick={dialogTab === 0 ? inviteSingle : inviteCsv}
          >
            {inviteLoading ? <CircularProgress size={20} /> : (dialogTab === 0 ? 'Wyślij zaproszenie' : 'Zaproś z CSV')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Employees;
